import { useState, useEffect, useRef } from 'react'
import { Database, Loader2, CheckCircle, XCircle, RefreshCw, Info, ChevronLeft, ChevronRight, Search, GripVertical, Zap, Clock } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Switch } from '@/shared/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  useCreateConfiguracion,
  useUpdateConfiguracion,
  useTestConexion,
  useGrupos,
} from '@/modules/registros/hooks/useConfiguraciones'
import type { ConfiguracionRegistros, ConfiguracionFormData, ColumnaTabla } from '../types'
import { VisualizacionStep, defaultVisualizacionConfig } from './VisualizacionStep'

const DB_ENGINES = [
  { value: 'mysql', label: 'MySQL', port: '3306' },
  { value: 'postgresql', label: 'PostgreSQL', port: '5432' },
  { value: 'mssql', label: 'SQL Server', port: '1433' },
  { value: 'sqlite', label: 'SQLite', port: '' },
]

interface ConfiguracionFormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  configuracion?: ConfiguracionRegistros | null
}

const defaultFormData: ConfiguracionFormData = {
  grupo_id: 0,
  db_host: '',
  db_port: '3306',
  db_name: '',
  db_user: '',
  db_password: '',
  db_engine: 'mysql',
  tabla_nombre: '',
  campos_mapeo: {},
  campos_mostrar: [],
  campos_filtros: [],
  campos_excel: [],
  visualizacion_config: defaultVisualizacionConfig,
  email_blacklist: '',
  titulo_reporte: 'Reporte de Registros',
  descripcion: '',
  url_registro: '',
  url_tratamiento_datos: 'https://tonalstrategies.com/politica-de-privacidad/',
  activo: true,
  asistencia_presencial: false,
  asistencia_tabla: 'asistencia',
  asistencia_datos_tabla: '',
  asistencia_base_wp: '',
  asistencia_fecha_evento: '',
  asistencia_hora_inicio: '',
  asistencia_hora_fin: '',
  campos_mostrar_asistencia: [],
}

// Formatea el nombre de una columna para mostrarla como encabezado legible.
// 'fecha_asistencia' es la fecha de check-in (tabla asistencia), no de la tabla de datos.
function formatColumnLabel(name: string): string {
  if (name === 'fecha_asistencia') return 'Fecha/Hora (check-in)'
  return name
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// Step Indicator Component
function StepIndicator({ currentStep }: { currentStep: 1 | 2 | 3 }) {
  const steps = [
    { number: 1, label: 'Conexión' },
    { number: 2, label: 'Campos' },
    { number: 3, label: 'Visualización' },
  ]

  return (
    <div className="flex items-center justify-center gap-1 mb-6">
      {steps.map((step, index) => (
        <div key={step.number} className="flex items-center">
          <div className="flex items-center gap-1.5">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                currentStep >= step.number
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {step.number}
            </div>
            <span className={`text-xs ${currentStep >= step.number ? 'font-medium' : 'text-muted-foreground'}`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700 mx-2">
              <div
                className={`h-full bg-teal-600 transition-all duration-300 ${
                  currentStep > step.number ? 'w-full' : 'w-0'
                }`}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Sortable Row Component
interface SortableRowProps {
  col: ColumnaTabla
  camposMostrar: string[]
  camposFiltros: string[]
  camposExcel: string[]
  onToggleMostrar: () => void
  onToggleFiltro: () => void
  onToggleExcel: () => void
}

function SortableRow({
  col,
  camposMostrar,
  camposFiltros,
  camposExcel,
  onToggleMostrar,
  onToggleFiltro,
  onToggleExcel,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.nombre })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'opacity-50 bg-muted' : ''}
    >
      <TableCell className="w-[40px] p-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell className="font-mono text-sm">{col.nombre}</TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs font-normal">
          {col.tipo}
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        <Checkbox
          checked={camposMostrar.includes(col.nombre)}
          onCheckedChange={onToggleMostrar}
        />
      </TableCell>
      <TableCell className="text-center">
        <Checkbox
          checked={camposFiltros.includes(col.nombre)}
          onCheckedChange={onToggleFiltro}
        />
      </TableCell>
      <TableCell className="text-center">
        <Checkbox
          checked={camposExcel.includes(col.nombre)}
          onCheckedChange={onToggleExcel}
        />
      </TableCell>
    </TableRow>
  )
}

// Campos Configuration Step Component
interface ConfiguracionCamposStepProps {
  estructuraTabla: ColumnaTabla[]
  tablaNombre: string
  camposMostrar: string[]
  camposFiltros: string[]
  camposExcel: string[]
  onCamposMostrarChange: (campos: string[]) => void
  onCamposFiltrosChange: (campos: string[]) => void
  onCamposExcelChange: (campos: string[]) => void
  onEstructuraReorder: (newEstructura: ColumnaTabla[]) => void
}

function ConfiguracionCamposStep({
  estructuraTabla,
  tablaNombre,
  camposMostrar,
  camposFiltros,
  camposExcel,
  onCamposMostrarChange,
  onCamposFiltrosChange,
  onCamposExcelChange,
  onEstructuraReorder,
}: ConfiguracionCamposStepProps) {
  const [searchTerm, setSearchTerm] = useState('')

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const filteredColumnas = estructuraTabla.filter((col) =>
    col.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const isSearching = searchTerm.trim() !== ''

  const toggleCampo = (
    campo: string,
    currentList: string[],
    setter: (campos: string[]) => void
  ) => {
    if (currentList.includes(campo)) {
      setter(currentList.filter((c) => c !== campo))
    } else {
      // Mantener el orden según la posición en estructuraTabla
      const newList = [...currentList, campo]
      const orderedList = estructuraTabla
        .filter((col) => newList.includes(col.nombre))
        .map((col) => col.nombre)
      setter(orderedList)
    }
  }

  const selectAll = (setter: (campos: string[]) => void) => {
    // Mantener el orden de estructuraTabla
    setter(estructuraTabla.map((col) => col.nombre))
  }

  const deselectAll = (setter: (campos: string[]) => void) => {
    setter([])
  }

  const selectAllMostrar = () => selectAll(onCamposMostrarChange)
  const selectAllFiltros = () => selectAll(onCamposFiltrosChange)
  const selectAllExcel = () => selectAll(onCamposExcelChange)

  const deselectAllMostrar = () => deselectAll(onCamposMostrarChange)
  const deselectAllFiltros = () => deselectAll(onCamposFiltrosChange)
  const deselectAllExcel = () => deselectAll(onCamposExcelChange)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = estructuraTabla.findIndex((col) => col.nombre === active.id)
      const newIndex = estructuraTabla.findIndex((col) => col.nombre === over.id)

      const newEstructura = arrayMove(estructuraTabla, oldIndex, newIndex)
      onEstructuraReorder(newEstructura)

      // Reordenar también los campos seleccionados
      const reorderList = (list: string[]) => {
        return newEstructura
          .filter((col) => list.includes(col.nombre))
          .map((col) => col.nombre)
      }

      onCamposMostrarChange(reorderList(camposMostrar))
      onCamposFiltrosChange(reorderList(camposFiltros))
      onCamposExcelChange(reorderList(camposExcel))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Configurar Campos</h3>
          <p className="text-xs text-muted-foreground">
            Tabla "{tablaNombre}" ({estructuraTabla.length} columnas) - Arrastra para reordenar
          </p>
        </div>
      </div>

      {/* Search and bulk actions */}
      {estructuraTabla.length > 10 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar columna..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {/* Bulk actions */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-muted-foreground">Mostrar:</span>
        <button
          type="button"
          onClick={selectAllMostrar}
          className="text-blue-600 hover:underline"
        >
          Todos
        </button>
        <span className="text-muted-foreground">/</span>
        <button
          type="button"
          onClick={deselectAllMostrar}
          className="text-blue-600 hover:underline"
        >
          Ninguno
        </button>
        <span className="text-muted-foreground mx-2">|</span>
        <span className="text-muted-foreground">Filtro:</span>
        <button
          type="button"
          onClick={selectAllFiltros}
          className="text-blue-600 hover:underline"
        >
          Todos
        </button>
        <span className="text-muted-foreground">/</span>
        <button
          type="button"
          onClick={deselectAllFiltros}
          className="text-blue-600 hover:underline"
        >
          Ninguno
        </button>
        <span className="text-muted-foreground mx-2">|</span>
        <span className="text-muted-foreground">Excel:</span>
        <button
          type="button"
          onClick={selectAllExcel}
          className="text-blue-600 hover:underline"
        >
          Todos
        </button>
        <span className="text-muted-foreground">/</span>
        <button
          type="button"
          onClick={deselectAllExcel}
          className="text-blue-600 hover:underline"
        >
          Ninguno
        </button>
      </div>

      {isSearching && (
        <p className="text-xs text-amber-600">
          El reordenamiento está deshabilitado mientras buscas
        </p>
      )}

      {/* Columns table */}
      <div className="border rounded-lg max-h-[400px] overflow-y-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table>
            <TableHeader className="sticky top-0 bg-white dark:bg-card z-10">
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="w-[160px]">Columna</TableHead>
                <TableHead className="w-[100px]">Tipo</TableHead>
                <TableHead className="w-[80px] text-center">Mostrar</TableHead>
                <TableHead className="w-[80px] text-center">Filtro</TableHead>
                <TableHead className="w-[80px] text-center">Excel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isSearching ? (
                // Sin drag and drop durante búsqueda
                filteredColumnas.map((col) => (
                  <TableRow key={col.nombre}>
                    <TableCell className="w-[40px] p-2">
                      <div className="p-1">
                        <GripVertical className="h-4 w-4 text-muted-foreground/30" />
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{col.nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-normal">
                        {col.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={camposMostrar.includes(col.nombre)}
                        onCheckedChange={() =>
                          toggleCampo(col.nombre, camposMostrar, onCamposMostrarChange)
                        }
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={camposFiltros.includes(col.nombre)}
                        onCheckedChange={() =>
                          toggleCampo(col.nombre, camposFiltros, onCamposFiltrosChange)
                        }
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={camposExcel.includes(col.nombre)}
                        onCheckedChange={() =>
                          toggleCampo(col.nombre, camposExcel, onCamposExcelChange)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                // Con drag and drop
                <SortableContext
                  items={estructuraTabla.map((col) => col.nombre)}
                  strategy={verticalListSortingStrategy}
                >
                  {estructuraTabla.map((col) => (
                    <SortableRow
                      key={col.nombre}
                      col={col}
                      camposMostrar={camposMostrar}
                      camposFiltros={camposFiltros}
                      camposExcel={camposExcel}
                      onToggleMostrar={() =>
                        toggleCampo(col.nombre, camposMostrar, onCamposMostrarChange)
                      }
                      onToggleFiltro={() =>
                        toggleCampo(col.nombre, camposFiltros, onCamposFiltrosChange)
                      }
                      onToggleExcel={() =>
                        toggleCampo(col.nombre, camposExcel, onCamposExcelChange)
                      }
                    />
                  ))}
                </SortableContext>
              )}
            </TableBody>
          </Table>
        </DndContext>
      </div>

      {/* Summary */}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span>Mostrar: {camposMostrar.length}</span>
        <span>Filtros: {camposFiltros.length}</span>
        <span>Excel: {camposExcel.length}</span>
      </div>
    </div>
  )
}

export function ConfiguracionFormSheet({
  open,
  onOpenChange,
  configuracion,
}: ConfiguracionFormSheetProps) {
  const [formData, setFormData] = useState<ConfiguracionFormData>(defaultFormData)
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1)
  const [estructuraTabla, setEstructuraTabla] = useState<ColumnaTabla[] | null>(null)
  const [showTitleInfo, setShowTitleInfo] = useState(false)
  const [showBlacklistInfo, setShowBlacklistInfo] = useState(false)
  const [testResult, setTestResult] = useState<{
    status: 'idle' | 'success' | 'error'
    message: string
    estructura?: Array<{ nombre: string; tipo: string }>
    tablas?: string[]
  }>({ status: 'idle', message: '' })

  // Track previous tabla_nombre to detect changes
  const prevTablaNombre = useRef(formData.tabla_nombre)
  // Track configuration ID that has been initialized (null for new, number for edit)
  const initializedForConfigId = useRef<number | 'new' | null>(null)

  const { data: grupos, isLoading: gruposLoading } = useGrupos()
  const createMutation = useCreateConfiguracion()
  const updateMutation = useUpdateConfiguracion()
  const testConexionMutation = useTestConexion()

  const isEditing = !!configuracion

  // Initialize form when modal opens or when editing and configuracion arrives
  useEffect(() => {
    // Reset when modal closes
    if (!open) {
      initializedForConfigId.current = null
      return
    }

    // Determine what we should initialize for
    const targetConfigId = configuracion?.id ?? 'new'

    // Skip if already initialized for this configuration
    if (initializedForConfigId.current === targetConfigId) {
      return
    }

    // Mark as initialized for this config
    initializedForConfigId.current = targetConfigId

    if (configuracion) {
      setFormData({
        grupo_id: configuracion.grupo_id,
        db_host: configuracion.db_host,
        db_port: configuracion.db_port,
        db_name: configuracion.db_name,
        db_user: configuracion.db_user,
        db_password: '', // Don't show existing password
        db_engine: configuracion.db_engine,
        tabla_nombre: configuracion.tabla_nombre,
        campos_mapeo: configuracion.campos_mapeo || {},
        campos_mostrar: configuracion.campos_mostrar || [],
        campos_filtros: configuracion.campos_filtros || [],
        campos_excel: configuracion.campos_excel || [],
        visualizacion_config: {
          cards: {
            habilitado: configuracion.visualizacion_config?.cards?.habilitado ?? defaultVisualizacionConfig.cards.habilitado,
            cards: configuracion.visualizacion_config?.cards?.cards ?? defaultVisualizacionConfig.cards.cards,
          },
          graficas_verticales: {
            habilitado: configuracion.visualizacion_config?.graficas_verticales?.habilitado ?? defaultVisualizacionConfig.graficas_verticales?.habilitado ?? false,
            graficas: configuracion.visualizacion_config?.graficas_verticales?.graficas ?? defaultVisualizacionConfig.graficas_verticales?.graficas ?? [],
          },
          graficas_horizontales: {
            habilitado: configuracion.visualizacion_config?.graficas_horizontales?.habilitado ?? defaultVisualizacionConfig.graficas_horizontales?.habilitado ?? false,
            graficas: configuracion.visualizacion_config?.graficas_horizontales?.graficas ?? defaultVisualizacionConfig.graficas_horizontales?.graficas ?? [],
          },
          graficas_lineas: {
            habilitado: configuracion.visualizacion_config?.graficas_lineas?.habilitado ?? defaultVisualizacionConfig.graficas_lineas?.habilitado ?? false,
            graficas: configuracion.visualizacion_config?.graficas_lineas?.graficas ?? defaultVisualizacionConfig.graficas_lineas?.graficas ?? [],
          },
        },
        email_blacklist: configuracion.email_blacklist || '',
        titulo_reporte: configuracion.titulo_reporte,
        descripcion: configuracion.descripcion || '',
        url_registro: configuracion.url_registro || '',
        url_tratamiento_datos: configuracion.url_tratamiento_datos || '',
        activo: configuracion.activo,
        asistencia_presencial: configuracion.asistencia_presencial || false,
        asistencia_tabla: configuracion.asistencia_tabla || 'asistencia',
        asistencia_datos_tabla: configuracion.asistencia_datos_tabla || '',
        asistencia_base_wp: configuracion.asistencia_base_wp || '',
        asistencia_fecha_evento: configuracion.asistencia_fecha_evento || '',
        asistencia_hora_inicio: configuracion.asistencia_hora_inicio || '',
        asistencia_hora_fin: configuracion.asistencia_hora_fin || '',
        campos_mostrar_asistencia: configuracion.campos_mostrar_asistencia || [],
      })
      prevTablaNombre.current = configuracion.tabla_nombre
    } else {
      setFormData(defaultFormData)
      prevTablaNombre.current = ''
    }

    // Reset UI state on initial open
    setTestResult({ status: 'idle', message: '' })
    setCurrentStep(1)
    setEstructuraTabla(null)
  }, [open, configuracion])

  // Reset campos configuration when tabla_nombre changes
  useEffect(() => {
    if (prevTablaNombre.current !== formData.tabla_nombre) {
      setEstructuraTabla(null)
      setFormData(prev => ({
        ...prev,
        campos_mostrar: [],
        campos_filtros: [],
        campos_excel: [],
      }))
      setTestResult({ status: 'idle', message: '' })
      if (currentStep === 2) {
        setCurrentStep(1)
      }
      prevTablaNombre.current = formData.tabla_nombre
    }
  }, [formData.tabla_nombre, currentStep])

  const handleInputChange = (field: keyof ConfiguracionFormData, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleEngineChange = (engine: string) => {
    const engineConfig = DB_ENGINES.find((e) => e.value === engine)
    setFormData((prev) => ({
      ...prev,
      db_engine: engine,
      db_port: engineConfig?.port || prev.db_port,
    }))
  }

  const handleTestConexion = async () => {
    setTestResult({ status: 'idle', message: 'Probando conexión...' })

    try {
      const result = await testConexionMutation.mutateAsync({
        db_host: formData.db_host,
        db_port: formData.db_port,
        db_name: formData.db_name,
        db_user: formData.db_user,
        db_password: formData.db_password || undefined,
        db_engine: formData.db_engine,
        tabla_nombre: formData.tabla_nombre || undefined,
        config_id: isEditing && configuracion ? configuracion.id : undefined,
      })

      if (result.exito) {
        setTestResult({
          status: 'success',
          message: `Conexión exitosa (${result.tiempo_ms?.toFixed(0)}ms)`,
          estructura: result.estructura_tabla,
          tablas: result.tablas_disponibles,
        })
        // Save structure for Step 2
        if (result.estructura_tabla) {
          let estructura = result.estructura_tabla as ColumnaTabla[]

          // Si estamos editando, reordenar la estructura según el orden guardado en campos_mostrar
          if (isEditing && configuracion?.campos_mostrar?.length) {
            const savedOrder = configuracion.campos_mostrar
            estructura = [...estructura].sort((a, b) => {
              const indexA = savedOrder.indexOf(a.nombre)
              const indexB = savedOrder.indexOf(b.nombre)
              // Campos en el orden guardado van primero, luego los demás
              if (indexA === -1 && indexB === -1) return 0
              if (indexA === -1) return 1
              if (indexB === -1) return -1
              return indexA - indexB
            })
          }

          setEstructuraTabla(estructura)
        }
      } else {
        setTestResult({
          status: 'error',
          message: result.mensaje,
        })
      }
    } catch (error) {
      setTestResult({
        status: 'error',
        message: (error as Error).message || 'Error al probar la conexión',
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields for creation
    if (!isEditing && !formData.db_password) {
      alert('La contraseña de base de datos es requerida')
      return
    }

    // Validate at least one campo_mostrar selected
    if (formData.campos_mostrar.length === 0) {
      alert('Debes seleccionar al menos una columna para mostrar')
      return
    }

    // Construir campos_mapeo automáticamente a partir de los campos seleccionados
    // El mapeo es identidad: nombre_columna -> nombre_columna (campo estándar = columna real)
    const allSelectedCampos = new Set([
      ...formData.campos_mostrar,
      ...formData.campos_filtros,
      ...formData.campos_excel,
    ])
    const camposMapeoAuto: Record<string, string> = {}
    allSelectedCampos.forEach((campo) => {
      camposMapeoAuto[campo] = campo
    })

    // Destructure to exclude db_password from the spread
    const { db_password, ...restFormData } = formData

    const payload = {
      ...restFormData,
      campos_mapeo: camposMapeoAuto,
      asistencia_fecha_evento: formData.asistencia_fecha_evento || null,
      asistencia_hora_inicio: formData.asistencia_hora_inicio || null,
      asistencia_hora_fin: formData.asistencia_hora_fin || null,
      // Only include password if provided (required for create, optional for update)
      ...(db_password ? { db_password } : {}),
    }

    try {
      if (isEditing && configuracion) {
        await updateMutation.mutateAsync({ id: configuracion.id, payload })
      } else {
        await createMutation.mutateAsync(payload)
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving configuration:', error)
    }
  }

  const canProceedToStep2 = () => {
    return (
      formData.grupo_id !== 0 &&
      formData.db_host.trim() !== '' &&
      formData.db_name.trim() !== '' &&
      formData.db_user.trim() !== '' &&
      formData.tabla_nombre.trim() !== '' &&
      testResult.status === 'success' &&
      estructuraTabla !== null
    )
  }

  const canProceedToStep3 = () => {
    return formData.campos_mostrar.length > 0
  }

  const canSubmit = () => {
    const config = formData.visualizacion_config
    // Validar cards si están habilitadas
    if (config?.cards?.habilitado) {
      const cards = config.cards.cards ?? []
      if (cards.length === 0) return false
      // Cada card debe tener título y columna
      for (const card of cards) {
        if (!card.titulo?.trim() || !card.columna) return false
      }
    }
    // Validar gráfica si está habilitada
    if (config?.grafica_barras?.habilitado) {
      if (!config.grafica_barras.columna_agrupacion) return false
    }
    return true
  }

  const handleNextStep = (e: React.MouseEvent) => {
    // Prevent click event from propagating to avoid triggering submit on step change
    e.preventDefault()
    e.stopPropagation()

    if (currentStep === 1 && canProceedToStep2()) {
      // Use setTimeout to ensure click event completes before UI changes
      setTimeout(() => setCurrentStep(2), 0)
    } else if (currentStep === 2 && canProceedToStep3()) {
      setTimeout(() => setCurrentStep(3), 0)
    }
  }

  const handlePreviousStep = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (currentStep === 2) {
      setTimeout(() => setCurrentStep(1), 0)
    } else if (currentStep === 3) {
      setTimeout(() => setCurrentStep(2), 0)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  // Columnas seleccionables para la tabla de asistencias: fecha de check-in +
  // todas las columnas de la tabla de datos (detectadas al probar la conexión).
  const columnasAsistenciaDisponibles = [
    'fecha_asistencia',
    ...(estructuraTabla?.map((col) => col.nombre) ?? []),
  ]

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {isEditing ? 'Editar Configuración' : 'Nueva Configuración de Registros'}
            <button
              type="button"
              onClick={() => setShowTitleInfo(!showTitleInfo)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Info className="h-4 w-4" />
            </button>
            {showTitleInfo && (
              <span className="text-muted-foreground text-xs animate-in slide-in-from-left-2 duration-200">
                {isEditing
                  ? 'Modifica los parámetros de conexión y configuración del reporte.'
                  : 'Configura una nueva conexión a una base de datos externa.'}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <StepIndicator currentStep={currentStep} />

        <form onSubmit={handleSubmit} className="space-y-6">
          {currentStep === 1 ? (
            <>
              {/* Grupo */}
              <div className="space-y-2">
                <Label htmlFor="grupo">Grupo *</Label>
                <Select
                  value={formData.grupo_id.toString()}
                  onValueChange={(value) => handleInputChange('grupo_id', parseInt(value))}
                  disabled={gruposLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un grupo" />
                  </SelectTrigger>
                  <SelectContent>
                    {grupos?.map((grupo) => (
                      <SelectItem key={grupo.grupo_id} value={grupo.grupo_id.toString()}>
                        {grupo.grupo_nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Tipo de Reporte */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Tipo de Reporte</h3>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('asistencia_presencial', false)}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                      !formData.asistencia_presencial
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <Database className={`h-5 w-5 ${!formData.asistencia_presencial ? 'text-blue-600' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-medium ${!formData.asistencia_presencial ? 'text-blue-700 dark:text-blue-400' : 'text-muted-foreground'}`}>
                      Asíncrono
                    </span>
                    <span className="text-xs text-muted-foreground text-center">Reporte estático con filtros</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInputChange('asistencia_presencial', true)}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                      formData.asistencia_presencial
                        ? 'border-green-500 bg-green-50 dark:bg-green-950'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <Zap className={`h-5 w-5 ${formData.asistencia_presencial ? 'text-green-600' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-medium ${formData.asistencia_presencial ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
                      En Vivo
                    </span>
                    <span className="text-xs text-muted-foreground text-center">Dashboard con auto-refresh</span>
                  </button>
                </div>
              </div>

              <Separator />

              {/* Conexión a Base de Datos */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Conexión a Base de Datos</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="db_engine">Motor de BD *</Label>
                    <Select value={formData.db_engine} onValueChange={handleEngineChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DB_ENGINES.map((engine) => (
                          <SelectItem key={engine.value} value={engine.value}>
                            {engine.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="db_port">Puerto</Label>
                    <Input
                      id="db_port"
                      value={formData.db_port}
                      onChange={(e) => handleInputChange('db_port', e.target.value)}
                      placeholder="3306"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="db_host">Host / IP *</Label>
                  <Input
                    id="db_host"
                    value={formData.db_host}
                    onChange={(e) => handleInputChange('db_host', e.target.value)}
                    placeholder="localhost o 192.168.1.100"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="db_name">Nombre de la Base de Datos *</Label>
                  <Input
                    id="db_name"
                    value={formData.db_name}
                    onChange={(e) => handleInputChange('db_name', e.target.value)}
                    placeholder="mi_base_datos"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="db_user">Usuario *</Label>
                    <Input
                      id="db_user"
                      value={formData.db_user}
                      onChange={(e) => handleInputChange('db_user', e.target.value)}
                      placeholder="root"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="db_password">
                      Contraseña {isEditing ? '(dejar vacío para no cambiar)' : '*'}
                    </Label>
                    <Input
                      id="db_password"
                      type="password"
                      value={formData.db_password}
                      onChange={(e) => handleInputChange('db_password', e.target.value)}
                      placeholder="••••••••"
                      required={!isEditing}
                    />
                  </div>
                </div>

                {/* Tabla principal (siempre visible) */}
                <div className="space-y-2">
                  <Label htmlFor="tabla_nombre">Nombre de la Tabla *</Label>
                  <Input
                    id="tabla_nombre"
                    value={formData.tabla_nombre}
                    onChange={(e) => handleInputChange('tabla_nombre', e.target.value)}
                    placeholder="registros"
                    required
                  />
                </div>

                {/* Tablas adicionales para reporte En Vivo */}
                {formData.asistencia_presencial && (
                  <div className="space-y-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/30 p-4">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-green-600" />
                      <h4 className="text-sm font-semibold text-green-700 dark:text-green-400">Tablas del Dashboard en Vivo</h4>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tablas adicionales para el dashboard de asistencias en tiempo real. La tabla de registros principal se usa para el reporte estándar.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="asistencia_tabla">
                          Tabla de Asistencias *
                          <span className="ml-1 text-xs text-muted-foreground">(check-in)</span>
                        </Label>
                        <Input
                          id="asistencia_tabla"
                          value={formData.asistencia_tabla}
                          onChange={(e) => handleInputChange('asistencia_tabla', e.target.value)}
                          placeholder="asistencia"
                        />
                        <p className="text-xs text-muted-foreground">Debe tener columna <code className="bg-muted px-1 rounded">id_registro</code></p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="asistencia_datos_tabla">
                          Tabla de Datos Completos *
                          <span className="ml-1 text-xs text-muted-foreground">(registros)</span>
                        </Label>
                        <Input
                          id="asistencia_datos_tabla"
                          value={formData.asistencia_datos_tabla}
                          onChange={(e) => handleInputChange('asistencia_datos_tabla', e.target.value)}
                          placeholder="registros_audarq14"
                        />
                        <p className="text-xs text-muted-foreground">JOIN: <code className="bg-muted px-1 rounded">asistencia.id_registro = datos.id</code></p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Test Connection Button */}
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestConexion}
                    disabled={testConexionMutation.isPending || !formData.db_host || !formData.db_name}
                  >
                    {testConexionMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Probar Conexión
                  </Button>

                  {testResult.status === 'success' && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">{testResult.message}</span>
                    </div>
                  )}

                  {testResult.status === 'error' && (
                    <div className="flex items-center gap-2 text-destructive">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">{testResult.message}</span>
                    </div>
                  )}
                </div>

                {/* Show available tables if no table name was specified */}
                {testResult.tablas && testResult.tablas.length > 0 && (
                  <div className="rounded-lg border p-3 bg-muted/50">
                    <p className="text-sm font-medium mb-2">
                      Tablas disponibles ({testResult.tablas.length}):
                    </p>
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                      {testResult.tablas.map((tabla) => (
                        <Badge
                          key={tabla}
                          variant="outline"
                          className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                          onClick={() => {
                            handleInputChange('tabla_nombre', tabla)
                            setTestResult({ status: 'idle', message: '' })
                          }}
                        >
                          {tabla}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Haz clic en una tabla para seleccionarla
                    </p>
                  </div>
                )}

                {/* Show table structure if test was successful */}
                {testResult.estructura && testResult.estructura.length > 0 && (
                  <div className="rounded-lg border p-3 bg-muted/50">
                    <p className="text-sm font-medium mb-2">Columnas detectadas:</p>
                    <div className="flex flex-wrap gap-1">
                      {testResult.estructura.map((col) => (
                        <Badge key={col.nombre} variant="secondary" className="text-xs">
                          {col.nombre}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Filtros del Evento (solo En Vivo) */}
              {formData.asistencia_presencial && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <Clock className="h-4 w-4" /> Filtros del Evento
                    </h3>

                    <div className="space-y-2">
                      <Label htmlFor="asistencia_base_wp">Identificador del Evento (base_wp)</Label>
                      <Input
                        id="asistencia_base_wp"
                        value={formData.asistencia_base_wp}
                        onChange={(e) => handleInputChange('asistencia_base_wp', e.target.value)}
                        placeholder="audarq14"
                      />
                      <p className="text-xs text-muted-foreground">Valor de la columna <code className="bg-muted px-1 rounded">base_wp</code> para filtrar este evento específico</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="asistencia_fecha_evento">Fecha del Evento</Label>
                      <Input
                        id="asistencia_fecha_evento"
                        type="date"
                        value={formData.asistencia_fecha_evento}
                        onChange={(e) => handleInputChange('asistencia_fecha_evento', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="asistencia_hora_inicio">Hora de Inicio</Label>
                        <Input
                          id="asistencia_hora_inicio"
                          type="time"
                          value={formData.asistencia_hora_inicio}
                          onChange={(e) => handleInputChange('asistencia_hora_inicio', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="asistencia_hora_fin">Hora de Fin</Label>
                        <Input
                          id="asistencia_hora_fin"
                          type="time"
                          value={formData.asistencia_hora_fin}
                          onChange={(e) => handleInputChange('asistencia_hora_fin', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Configuración del Reporte */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Configuración del Reporte</h3>

                <div className="space-y-2">
                  <Label htmlFor="titulo_reporte">Título del Reporte *</Label>
                  <Input
                    id="titulo_reporte"
                    value={formData.titulo_reporte}
                    onChange={(e) => handleInputChange('titulo_reporte', e.target.value)}
                    placeholder="Reporte de Registros"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Descripción del reporte..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url_registro">URL de Registro (botón Nuevo +)</Label>
                  <Input
                    id="url_registro"
                    type="url"
                    value={formData.url_registro}
                    onChange={(e) => handleInputChange('url_registro', e.target.value)}
                    placeholder="https://ejemplo.com/nuevo-registro"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url_tratamiento_datos">URL Política de Privacidad</Label>
                  <Input
                    id="url_tratamiento_datos"
                    type="url"
                    value={formData.url_tratamiento_datos}
                    onChange={(e) => handleInputChange('url_tratamiento_datos', e.target.value)}
                    placeholder="https://tonalstrategies.com/politica-de-privacidad/"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_blacklist" className="flex items-center gap-2">
                    Emails en Blacklist
                    <button
                      type="button"
                      onClick={() => setShowBlacklistInfo(!showBlacklistInfo)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    {showBlacklistInfo && (
                      <span className="text-muted-foreground text-xs animate-in slide-in-from-left-2 duration-200">
                        Emails a excluir de los reportes, separados por comas
                      </span>
                    )}
                  </Label>
                  <Textarea
                    id="email_blacklist"
                    value={formData.email_blacklist}
                    onChange={(e) => handleInputChange('email_blacklist', e.target.value)}
                    placeholder="test@ejemplo.com, prueba@test.com"
                    rows={2}
                  />
                </div>
              </div>

              <Separator />

              {/* Estado */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="activo">Configuración Activa</Label>
                  <p className="text-sm text-muted-foreground">
                    Los usuarios del grupo podrán acceder al reporte
                  </p>
                </div>
                <Switch
                  id="activo"
                  checked={formData.activo}
                  onCheckedChange={(checked) => handleInputChange('activo', checked)}
                />
              </div>
            </>
          ) : currentStep === 2 ? (
            <>
              {/* Step 2: Configuración de Campos */}
              {estructuraTabla && (
                <ConfiguracionCamposStep
                  estructuraTabla={estructuraTabla}
                  tablaNombre={formData.tabla_nombre}
                  camposMostrar={formData.campos_mostrar}
                  camposFiltros={formData.campos_filtros}
                  camposExcel={formData.campos_excel}
                  onCamposMostrarChange={(campos) =>
                    setFormData((prev) => ({ ...prev, campos_mostrar: campos }))
                  }
                  onCamposFiltrosChange={(campos) =>
                    setFormData((prev) => ({ ...prev, campos_filtros: campos }))
                  }
                  onCamposExcelChange={(campos) =>
                    setFormData((prev) => ({ ...prev, campos_excel: campos }))
                  }
                  onEstructuraReorder={setEstructuraTabla}
                />
              )}

              {/* Columnas de la tabla de asistencias (solo modo En Vivo) */}
              {formData.asistencia_presencial && (
                <div className="space-y-3 rounded-lg border border-gray-300 dark:border-gray-700 p-4">
                  <div>
                    <h3 className="font-semibold text-sm">Columnas de la tabla de asistencias</h3>
                    <p className="text-xs text-muted-foreground">
                      Selecciona qué columnas se mostrarán en el dashboard de asistencias. Si no marcas ninguna se muestran todas.
                    </p>
                  </div>
                  {estructuraTabla ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {columnasAsistenciaDisponibles.map((colName) => {
                        const seleccion = formData.campos_mostrar_asistencia
                        const checked = seleccion.length === 0 || seleccion.includes(colName)
                        const toggle = () => {
                          const efectivo = seleccion.length === 0 ? columnasAsistenciaDisponibles : seleccion
                          const siguiente = efectivo.includes(colName)
                            ? efectivo.filter((k) => k !== colName)
                            : [...efectivo, colName]
                          const ordenado = columnasAsistenciaDisponibles.filter((k) => siguiente.includes(k))
                          setFormData((prev) => ({ ...prev, campos_mostrar_asistencia: ordenado }))
                        }
                        return (
                          <label key={colName} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={checked} onCheckedChange={toggle} />
                            {formatColumnLabel(colName)}
                          </label>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Prueba la conexión en el paso 1 para cargar las columnas disponibles.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Step 3: Configuración de Visualización */}
              {estructuraTabla && (
                <VisualizacionStep
                  config={formData.visualizacion_config}
                  columnas={estructuraTabla}
                  onChange={(config) =>
                    setFormData((prev) => ({ ...prev, visualizacion_config: config }))
                  }
                />
              )}
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between gap-3 pt-4">
            {currentStep === 1 ? (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleNextStep}
                  disabled={!canProceedToStep2()}
                  className="bg-teal-800 hover:bg-teal-900 text-white"
                >
                  Siguiente
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            ) : currentStep === 2 ? (
              <>
                <Button type="button" variant="outline" onClick={handlePreviousStep}>
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  type="button"
                  onClick={handleNextStep}
                  disabled={!canProceedToStep3()}
                  className="bg-teal-800 hover:bg-teal-900 text-white"
                >
                  Siguiente
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={handlePreviousStep}>
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  type="submit"
                  disabled={isPending || !canSubmit()}
                  className="bg-teal-800 hover:bg-teal-900 text-white"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEditing ? 'Guardar Cambios' : 'Crear Configuración'}
                </Button>
              </>
            )}
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
